const { Core, Utils, Cache } = Global;

const {
    $fetch,
    debounce,
    $toggleDisplay,
    $replaceContent,
    $active,
    $loading,
    setDocumentClickHandler
} = Utils;

const RQ_SECTION = 'r_predictive-search';
const RQ_SECTION_SELECTOR = '[data-template]';
const ATTR_NO_RESULTS = 'data-empty-results';
const DEFAULT_MIN_CHARS = 2;
const DEFAULT_LIMIT = 10;
const DEBOUNCE_INTERVAL = 300;

customElements.define('predictive-search', class extends Core {
    propTypes = {
        'results-limit': Number
    }

    elements = {
        $: [
            'search-input',
            'product-type-select',
            'button',
            'results',
            'initial'
        ]
    }

    render() {
        this.$('search-input', {
            focus: this._handleFocus,
            input: debounce(
                this._handleInput.bind(this),
                DEBOUNCE_INTERVAL
            )
        });

        this.$('button', {
            click: this._handleButtonClick
        });

        this.$('product-type-select', {
            change: this._handleTypeSelect
        });

        this._toggleDocClickListener = setDocumentClickHandler(
            this._handleOuterClick.bind(this)
        );
    }

    _handleTypeSelect() {
        this._handleInput();
        this.$('search-input').focus();
    }

    _handleButtonClick(e) {
        if (this.$('search-input').value) return;

        e.preventDefault();
        this.$('search-input').focus();
    }

    _handleFocus() {
        this.active =
            this.minQueryChars ||
            !!this.$('initial');
    }

    async _handleInput() {
        if (!this.minQueryChars) {
            this.showInitial = true;
            this.showResults = false;
            return;
        }

        await this._getQueryResults();
        this._renderQueryResults();
        this.active = true;
    }

    async _getQueryResults() {
        if (!Cache.has(this.query)) {
            const $queryDoc =
                await this._fetchQueryDoc();

            Cache.set(this.query, {
                hasResults:
                    !$queryDoc.hasAttribute(
                        ATTR_NO_RESULTS
                    ),
                content: $queryDoc
            });
        }
    }

    async _fetchQueryDoc() {
        return $fetch(
            `${routes.predictive_search_url}`,
            {
                params: {
                    'q': this.query,
                    'resources[type]': 'product',
                    'resources[limit]': this.limit,
                    'resources[options][unavailable_products]':
                        'last',
                    'section_id': RQ_SECTION
                },
                before: this._loading(true),
                after: this._loading(false),
                select: RQ_SECTION_SELECTOR
            }
        );
    }

    _renderQueryResults() {
        const currentQuery =
            Cache.get(this.query);

        $replaceContent(
            this.$('results'),
            currentQuery.content
        );

        this.showInitial =
            !currentQuery.hasResults;

        this.showResults = true;

        if (currentQuery.hasResults) {
            this._addViewAllButton();
        }
    }

    _addViewAllButton() {
        const resultsContainer =
            this.$('results');

        const searchTerm =
            this.$('search-input')
                .value
                .trim();

        if (!resultsContainer || !searchTerm) {
            return;
        }

        const oldButton =
            resultsContainer.querySelector(
                '.predictive-search-view-all'
            );

        if (oldButton) {
            oldButton.remove();
        }

        const buttonWrapper =
            document.createElement('div');

        buttonWrapper.className =
            'predictive-search-view-all';

        buttonWrapper.style.padding =
            '16px 20px 20px';

        buttonWrapper.style.borderTop =
            '1px solid rgba(0, 0, 0, 0.10)';

        buttonWrapper.style.marginTop =
            '12px';

        const button =
            document.createElement('a');

        const shopRoot =
            window.Shopify &&
            window.Shopify.routes &&
            window.Shopify.routes.root
                ? window.Shopify.routes.root
                : '/';

        button.href =
            `${shopRoot}search?q=` +
            `${encodeURIComponent(searchTerm)}` +
            `&type=product`;

        button.textContent =
            `View all results for “${searchTerm}”`;

        button.style.display = 'block';
        button.style.width = '100%';
        button.style.boxSizing =
            'border-box';
        button.style.padding =
            '14px 20px';
        button.style.borderRadius = '6px';

        button.style.background =
            'linear-gradient(' +
            '90deg, ' +
            '#c933ad 0%, ' +
            '#655dc6 100%' +
            ')';

        button.style.color = '#ffffff';
        button.style.fontSize = '16px';
        button.style.fontWeight = '700';
        button.style.lineHeight = '1.3';
        button.style.textAlign = 'center';
        button.style.textDecoration =
            'none';
        button.style.cursor = 'pointer';

        button.setAttribute(
            'aria-label',
            `View all product results for ${searchTerm}`
        );

        buttonWrapper.appendChild(button);
        resultsContainer.appendChild(
            buttonWrapper
        );
    }

    _handleOuterClick(e) {
        if (
            !this.contains(e.target) &&
            this.active &&
            document.activeElement !==
                this.$('search-input')
        ) {
            this.active = false;
        }
    }

    _loading(state) {
        return () => {
            $loading(this, state);
        }
    }

    set showInitial(state) {
        if (!this.$('initial')) {
            this.active = false;
            return;
        }

        $toggleDisplay(
            this.$('initial'),
            state
        );

        this.active = state;
    }

    set showResults(state) {
        $toggleDisplay(
            this.$('results'),
            state
        );
    }

    set active(state) {
        if (state === this._active) {
            return;
        }

        this._toggleDocClickListener(state);
        $active(this, state);
        this._active = state;
    }

    get active() {
        return !!this._active;
    }

    get minQueryChars() {
        return (
            this.$('search-input')
                .value
                .trim()
                .length >= DEFAULT_MIN_CHARS
        );
    }

    get query() {
        const inputValue =
            this.$('search-input')
                .value
                .trim();

        if (
            this.$('product-type-select') &&
            this.$('product-type-select')
                .value !== ''
        ) {
            return (
                `product_type:` +
                `${this.$('product-type-select').value}` +
                ` AND ${inputValue}`
            );
        }

        return inputValue;
    }

    get limit() {
        return (
            this.prop('results-limit') ||
            DEFAULT_LIMIT
        );
    }
});